import {useState, useRef, useEffect} from 'react';
import {CommunicationManager} from './Tools/CommunicationManager';
import SearchTermSuggestions from './Component/SearchTermSuggestions';
import BlogSearchResults from './Component/BlogSearchResults';
import GenericSearchResults from './Component/GenericSearchResults';
import FaqSearchResults from './Component/FaqSearchResults';
import PromotedSearchResults from './Component/PromotedSearchResults';

export default function MainSearchApp() {
  const fadeTime = 700;
  const fadeTimeClass = `duration-[${fadeTime}ms]`;

  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [queryString, setQueryString] = useState('');

  const [searchFocus, setSearchFocus] = useState(false);
  const [renderDom, setRenderDom] = useState(false);
  const [blogResults, setBlogResults] = useState([]);
  const [genericResults, setGenericResults] = useState([]);
  const [faqResults, setFaqResults] = useState([]);
  const [promotedResults, setPromotedResults] = useState([]);

  const [filterType, setFilterType] = useState('all'); // New state for filter type
  const [searchRequestTimeout, setSearchRequestTimeout] = useState(null);

  const commManager = new CommunicationManager('headless_search');

  // Steal focus from whatever was triggering the app
  if (renderDom === true && searchInputRef.current) {
    searchInputRef.current.focus();
  }

  // Pass down renderDom to the showContents, which will then fade in using a class switch
  let showContents = renderDom;

  if (searchTerm.length < 2) {
    // Reset the results if there were any; without the if-statement we would cause a render loop
    if (blogResults.length) {
      setBlogResults([]);
    }
    if (genericResults.length) {
      setGenericResults([]);
    }
    if (faqResults.length) {
      setFaqResults([]);
    }
    if (promotedResults.length) {
      setPromotedResults([]);
    }

  } else {
    let newQueryString = 'q=' + encodeURIComponent(searchTerm);

    if (newQueryString !== queryString) {
      // Update query-string; this will execute the useEffect below which will execute the API call
      setQueryString(newQueryString);
    }
  }

  /**
   * Callback for searchterm input field and website section field (clean wildcard characters before setting it)
   * @param e
   */
  function handleSearch(e) {
    let searchTerm = searchInputRef.current.value.replace('*', '');

    // Let other (react) components know about the current searchTerm
    commManager.sendMessage({searchTerm});

    setSearchTerm(searchTerm);
  }

  function handleFilterClick(type) {
    setFilterType(type);
    const scrollContainer = document.querySelector('.search-overlay .header__overlay-inner');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function handleFormSubmit(e) {
    e.preventDefault();
  }

  /**
   * Submit on enter
   * @param e
   */
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTerm.length > 2) {
        // Trigger search logic
        setQueryString('q=' + encodeURIComponent(searchTerm));
      }
    }
  }

  // Execute this only during initial load
  useEffect(() => {
    commManager.receiveMessage((message) => {
      if ('searchTerm' in message) {
        console.debug('Received searchTerm ' + message.searchTerm);
        setSearchTerm(message.searchTerm);
      }
    });
  }, []);

  useEffect(() => {
    if (!queryString) {
      return;
    }

    let isMounted = true;


    if (searchRequestTimeout) {
      clearInterval(searchRequestTimeout);
    }

    setSearchRequestTimeout(setTimeout(() => {
      fetch('/api/search/public?' + queryString)
        .then(response => response.json())
        .then(data => {
          if (isMounted) {
            setBlogResults(data.blogs.data.results);
            setGenericResults(data.generic.data.results);
            setFaqResults(data.faqs.data.results);
            setPromotedResults(data.promoted.data.results);
          }
        })
    }, 300));
    return () => {
      isMounted = false;
    }

  }, [queryString]);

  return (
    <>
      <form ref={searchResultsRef} onSubmit={handleFormSubmit} data-fcs-region="search_app"
            className={'search-block__search-app ' + (searchTerm.length ? 'search-block__search-app--is-active' : '') + (searchTerm.length > 2 ? ' search-block__search-app--is-searching' : '')}>
        <div className="search-app__search-app-form">
          <div className="form--app">
            <div className="form-item">
              <div className="main-search main-search--overlay">
                <div className="main-search__input-wrapper">
                  <label className={'sr-only'} htmlFor="overlay-search-input">Zoek op trefwoord</label>
                  <input type="search" id="overlay-search-input" name="keys" onKeyDown={handleKeyDown}
                         title="Geef de woorden op waarnaar u wilt zoeken."
                         placeholder="Zoeken..." onChange={handleSearch} ref={searchInputRef} value={searchTerm}
                         className="main-search__input form-search" role="presentation" autoComplete="off"
                         autoCorrect="off" spellCheck="false"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        {searchTerm.length > 2 && (
          <div className="search-app__header">
            <p className={'search-app__thisterm'}>Resultaten voor "{searchTerm}"</p>
            <div className="search-app__filters">
              <h2 className={'search-app__filters__title search-overlay__subheader'}>Filter op</h2>
              <div className="search-app__filters__wrapper">
                <button type="button" className={filterType === 'all' ? 'active' : ''} onClick={() => handleFilterClick('all')} data-fcs-elm-type="filter-all">
                  Alles
                </button>
                <button type="button" className={filterType === 'faq' ? 'active' : ''} onClick={() => handleFilterClick('faq')} data-fcs-elm-type="filter-faq">
                  Faq
                </button>
                <button type="button" className={filterType === 'blog' ? 'active' : ''} onClick={() => handleFilterClick('blog')} data-fcs-elm-type="filter-blog">
                  Blog
                </button>
                <button type="button" className={filterType === 'generic' ? 'active' : ''} onClick={() => handleFilterClick('generic')} data-fcs-elm-type="filter-generic" >
                  Productinformatie
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={"search-app__results"}>
          {filterType === 'all' && searchTerm.length > 2 &&
            <ul className="search-results">
              <SearchTermSuggestions
                searchTerm={searchTerm}
                useSuggestion={(value) => {
                  setSearchTerm(value); // Update the search term when a suggestion is clicked
                }}
              />

              <li className={"search-results__item search-results__item--promoted"} key={'promoted-results'}
                  data-fcs-region="promoted_results">
                <div className={"search-results__item__header"}>
                  <h3 className={"search-results__heading"}>Voorgestelde pagina’s</h3>
                </div>
                <PromotedSearchResults searchTerm={searchTerm} searchResults={promotedResults} limit={2}/>
              </li>

              {genericResults.length > 0 &&
                <li className={"search-results__item search-results__item--generic"} key={'generic-results'}
                    data-fcs-region="generic_results">
                  <div className={"search-results__item__header js-button-target-default"}>
                    <h3 className={"search-results__heading"}>Gevonden pagina’s</h3>
                    <a onClick={() => handleFilterClick('generic')} data-fcs-elm-type="filter-generic">
                      Alle productinformatie
                    </a>
                  </div>
                  <GenericSearchResults searchTerm={searchTerm} searchResults={genericResults} limit={3}/>
                  <div className={'search-results__item__footer'}>
                    <a onClick={() => handleFilterClick('generic')} data-fcs-elm-type="filter-generic">
                      Alle productinformatie
                    </a>
                  </div>
                </li>
              }

              {faqResults.length > 0 &&
                <li className={"search-results__item search-results__item--faq"} key={'faq-results'}
                    data-fcs-region="faq_results">
                  <div className={"search-results__item__header js-button-target-default"}>
                    <h3 className={"search-results__heading"}>Belangrijke faq’s</h3>
                    <a onClick={() => handleFilterClick('faq')}
                       data-fcs-elm-type="filter-faq">
                      Alle faq's
                    </a>
                  </div>
                  <FaqSearchResults searchTerm={searchTerm} searchResults={faqResults} limit={5}/>
                  <div className={'search-results__item__footer'}>
                    <a onClick={() => handleFilterClick('faq')}
                       data-fcs-elm-type="filter-faq">
                      Alle faq's
                    </a>
                  </div>
                </li>
              }

              {blogResults.length > 0 &&
                <li className={"search-results__item search-results__item--blog"} key={'blog-results'}
                    data-fcs-region="blog_results">
                  <div className={"search-results__item__header js-button-target-default"}>
                    <h3 className={"search-results__heading"}>Gevonden
                      blogs</h3>
                    <a onClick={() => handleFilterClick('blog')}
                       data-fcs-elm-type="filter-blog">
                      Alle blogs
                    </a>
                  </div>
                  <BlogSearchResults searchTerm={searchTerm} searchResults={blogResults} limit={3}/>
                  <div className={'search-results__item__footer'}>
                    <a onClick={() => handleFilterClick('blog')}
                       data-fcs-elm-type="filter-blog">
                      Alle blogs
                    </a>
                  </div>
                </li>
              }
            </ul>
          }

          {/* Render More results for each filter, at the moment using the same component, this may change*/}
          {filterType === 'faq' && searchTerm.length > 2 && (
            <ul className="search-results--filtered">
              <li className={"search-results__item search-results__item--faq"} key={'faq-results'} data-fcs-region="faq_results_full">
                <div className={"search-results__item__header"}>
                  <h3 className={"search-results__heading"}>Belangrijke
                  faq’s</h3>
                </div>
                  <FaqSearchResults searchTerm={searchTerm} searchResults={faqResults} limit={1000}/>
              </li>
            </ul>
          )}

          {filterType === 'blog' && searchTerm.length > 2 && (
            <ul className="search-results--filtered">
              <li className={"search-results__item search-results__item--blog"} key={'blog-results'} data-fcs-region="blog_results_full">
                <div className={"search-results__item__header"}>
                  <h3 className={"search-results__heading"}>Gevonden
                  blogs</h3>
                </div>
                  <BlogSearchResults searchTerm={searchTerm} searchResults={blogResults} limit={1000}/>
              </li>
            </ul>
          )}

          {filterType === 'generic' && searchTerm.length > 2 && (
            <ul className="search-results--filtered">
              <li className={"search-results__item search-results__item--generic"} key={'generic-results'}
                  data-fcs-region="generic_results_full">
                  <div className={"search-results__item__header"}>
                    <h3 className={"search-results__heading"}>Gevonden pagina’s</h3>
                  </div>
                  <GenericSearchResults searchTerm={searchTerm} searchResults={genericResults} limit={1000}/>
              </li>
            </ul>
            )}

        </div>
      </form>
    </>
  );
}
